import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Typography, Button, Box, Grid, InputAdornment } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { Input, Select, Checkbox, DatePicker, DateTimePicker } from 'components/FormInputs';
import PATHS from 'constants/paths';
import { validateNewPatch, getMinMaxDates } from 'logic/bookingForm';
import moment from 'logic/moment';
import { useFormik } from 'components/shared/Formik';

const dateRange = getMinMaxDates();
const onlyNumbers = (value) => value.replace(/[^0-9]/, '');
const article =
  '66524-vilka-avbokningspolicys-kan-man-vaelja-mellan-i-stugknutens-foermedlingstjaenst';

const CreateForm = ({
  setPreviewMode,
  cancellationPolicy,
  cottages,
  initialValues,
  disableSubmit,
  isBookingStrict,
  conversationId,
}) => {
  const intl = useIntl();

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validateOnChange: false,
    validate: validateNewPatch({ intl }),
    onSubmit: async (values) => {
      return setPreviewMode(values);
    },
  });

  const {
    values,
    errors,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    pristine,
    isValid,
    handleChange,
    touched,
    handleBlur,
  } = formik;

  React.useEffect(() => {
    if (cottages && cottages.length === 1 && !values.cottage_id) {
      setFieldValue('cottage_id', cottages[0].id);
    }
  }, [cottages]);

  if (disableSubmit || !isBookingStrict) {
    return (
      <Typography paragraph>
        <>
          <FormattedMessage
            id="bookingForm.needMoreInfoHost"
            defaultMessage="You have to complete your profile before you can send an booking offer"
          />{' '}
          <Button
            variant="contained"
            color="primary"
            component={Link}
            size="small"
            to={`${PATHS.users.profile}?next=${PATHS.conversations.bookings.replace(':conversationId', conversationId)}`}
          >
            <FormattedMessage id="Click here" defaultMessage="Click here" />
          </Button>{' '}
          <FormattedMessage
            id="bookingForm.toFillOutTheForm"
            defaultMessage="to complete your profile."
          />
        </>
      </Typography>
    );
  }

  const cottage = cottages.find(({ id }) => id === parseInt(values.cottage_id, 10));

  let feeText = '';

  if (cottage && values.total_price >= 100 && values.total_price <= 100000) {
    const percentage = cottage.commission_percentage / 100;
    const fee = Math.round(values.total_price * percentage);
    const keep = values.total_price - fee;

    feeText = intl.formatMessage(
      {
        id: 'bookingForm.feeText',
        defaultMessage: 'Stugknutens fee is {fee} SEK, you keep {keep} SEK',
      },
      { fee, keep },
    );
  }

  return (
    <Box mb={2}>
      <form onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Select
              name="cottage_id"
              value={values.cottage_id}
              errors={errors}
              onChange={handleChange}
              onBlur={handleBlur}
              label={<FormattedMessage id="Listing" defaultMessage="Listing" />}
              options={cottages.map((c) => ({ value: c.id, label: `#${c.id} - ${c.title_sv}` }))}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DateTimePicker
              name="check_in"
              value={values.check_in || null}
              onBlur={handleBlur}
              onChange={(date) => {
                if (date) {
                  setFieldValue('check_in', date);

                  if (moment(values.check_out).isSame(initialValues.check_out, 'day')) {
                    setFieldValue('check_out', moment(date).add(1, 'day'));
                  }
                  if (moment(date).isBefore(moment(values.booking_expiration_date), 'day')) {
                    setFieldValue('booking_expiration_date', null);
                  }
                }

                if (date === null) {
                  setFieldValue('check_in', null);
                }
              }}
              label={<FormattedMessage id="bookingForm.checkIn" defaultMessage="Check in" />}
              cancelLabel={<FormattedMessage id="Cancel" defaultMessage="Cancel" />}
              clearLabel={<FormattedMessage id="Clear" defaultMessage="Clear" />}
              errors={errors}
              minDate={dateRange.min}
              maxDate={dateRange.max}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DateTimePicker
              name="check_out"
              value={values.check_out || null}
              onBlur={handleBlur}
              onChange={(date) => {
                if (date) {
                  setFieldValue('check_out', date);
                }

                if (date === null) {
                  setFieldValue('check_out', null);
                }
              }}
              label={<FormattedMessage id="bookingForm.checkOut" defaultMessage="Check out" />}
              cancelLabel={<FormattedMessage id="Cancel" defaultMessage="Cancel" />}
              clearLabel={<FormattedMessage id="Clear" defaultMessage="Clear" />}
              errors={errors}
              minDate={dateRange.min}
              maxDate={dateRange.max}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input
              name="number_of_guests"
              value={values.number_of_guests}
              onChange={(e) => {
                e.target.value = onlyNumbers(e.target.value);

                return handleChange(e);
              }}
              errors={errors}
              onBlur={handleBlur}
              label={
                <FormattedMessage
                  id="bookingForm.noOfGuests"
                  defaultMessage="Maximum number of guests"
                />
              }
              required
              type="number"
            />
          </Grid>
          <Grid item xs={12}>
            <Select
              name="cancellation_policy_id"
              value={values.cancellation_policy_id}
              errors={errors}
              onChange={(e) => {
                setFieldValue('booking_expiration_date', null);

                return handleChange(e);
              }}
              onBlur={handleBlur}
              label={
                <FormattedMessage
                  id="bookingForm.cancellationPolicy"
                  defaultMessage="Cancellation policy"
                />
              }
              options={cancellationPolicy.map(({ id, name }) => ({
                value: id,
                label: intl.formatMessage({
                  id: name,
                  defaultMessage: name,
                }),
              }))}
              help={
                window.supportHeroWidget && (
                  <>
                    <span
                      className="links"
                      onClick={() => window.supportHeroWidget.show({ article })}
                      role="button"
                      type="button"
                      tabIndex={-1}
                    >
                      <FormattedMessage id="Click here" defaultMessage="Click here" />
                    </span>{' '}
                    <FormattedMessage
                      id="bookingForm.cancellationPolicyHelp"
                      defaultMessage="to see a more detailed description
                      of the various policies you can choose from"
                    />
                  </>
                )
              }
              required
            />
          </Grid>
          <Grid item xs={12}>
            <Select
              name="cleaning_policy"
              value={values.cleaning_policy}
              errors={errors}
              onChange={handleChange}
              onBlur={handleBlur}
              label={<FormattedMessage id="bookingForm.cleaning" defaultMessage="Cleaning" />}
              options={[
                {
                  value: 'guest',
                  label: intl.formatMessage({
                    id: 'bookingForm.respGuest',
                    defaultMessage: 'Guest is responsible for cleaning',
                  }),
                },
                {
                  value: 'host',
                  label: intl.formatMessage({
                    id: 'bookingForm.respHost',
                    defaultMessage: 'Cleaning is done by host and included in price',
                  }),
                },
                {
                  value: 'external',
                  label: intl.formatMessage({
                    id: 'bookingForm.respExternal',
                    defaultMessage: 'Cleaning via cleaning company, paid separately',
                  }),
                },
              ]}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input
              name="total_price"
              value={values.total_price}
              errors={errors}
              onChange={(e) => {
                e.target.value = onlyNumbers(e.target.value);

                return handleChange(e);
              }}
              onBlur={handleBlur}
              label={<FormattedMessage id="bookingForm.totalPrice" defaultMessage="Total price" />}
              required
              type="number"
              pattern="[0-9]"
              InputProps={{
                endAdornment: <InputAdornment position="end">SEK</InputAdornment>,
              }}
              help={feeText}
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              name="additional_information"
              value={values.additional_information}
              errors={errors}
              onChange={handleChange}
              onBlur={handleBlur}
              label={
                <FormattedMessage
                  id="bookingForm.additionalInformation"
                  defaultMessage="Additional information"
                />
              }
              placeholder={intl.formatMessage({
                id: 'bookingForm.additionalInfoText',
                defaultMessage:
                  'Enter any further information you wish the guest to know before they make a booking.\n\nThis may, for example, concern whether pets are allowed, whether bed linen is included or not, whether cleaning services can be purchased or anything else the guest ought to know.',
              })}
              multiline
              rows={7}
            />
          </Grid>
          <Grid item xs={12}>
            <Checkbox
              name="mark_as_booked_if_accepted"
              value={values.mark_as_booked_if_accepted}
              label={
                <FormattedMessage
                  id="bookingForm.markAsBooked"
                  defaultMessage="Mark period as booked in calendar if guest accepts the offer. In case guest cancels the booking, mark the period as free again"
                />
              }
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <DatePicker
              name="booking_expiration_date"
              value={values.booking_expiration_date || null}
              onBlur={handleBlur}
              onChange={(date) => {
                if (date) {
                  setFieldValue('booking_expiration_date', date);
                }

                if (date === null) {
                  setFieldValue('booking_expiration_date', null);
                }
              }}
              label={
                <FormattedMessage
                  id="bookingForm.offerValidTill"
                  defaultMessage="Time limit for guest to accept?"
                />
              }
              cancelLabel={<FormattedMessage id="Cancel" defaultMessage="Cancel" />}
              clearLabel={<FormattedMessage id="Clear" defaultMessage="Clear" />}
              errors={errors}
              minDate={moment().format()}
              maxDate={moment(values.check_in).format()}
              required
              disabled={!values.check_in}
            />
            <Typography variant="body2">
              <FormattedMessage
                id="bookingForm.expirationRecommendation"
                defaultMessage="Guest accepts booking offer by paying the full rent"
              />
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              disabled={
                disableSubmit ||
                !isBookingStrict ||
                pristine ||
                !touched ||
                isSubmitting ||
                !isValid
              }
              type="submit"
            >
              <FormattedMessage id="Preview" defaultMessage="Preview" />
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

CreateForm.propTypes = {
  setPreviewMode: PropTypes.func.isRequired,
  cancellationPolicy: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  ),
  cottages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title_sv: PropTypes.string,
    }),
  ),
  initialValues: PropTypes.shape({
    check_out: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }),
  disableSubmit: PropTypes.bool,
  isBookingStrict: PropTypes.bool,
  conversationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default CreateForm;
